'use client'

import React, { useState, useCallback, useEffect } from 'react'
import { Calendar as BigCalendar, dateFnsLocalizer, Views } from 'react-big-calendar'
import { format, parse, startOfWeek, getDay, addDays, addWeeks, addYears, setHours, setMinutes, getHours, getMinutes } from 'date-fns'
import { enUS } from 'date-fns/locale'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/shared/ui/dialog"
import { Button } from "@/shared/ui/button"
import { Input } from "@/shared/ui/input"
import { DateInput } from "@/shared/ui/date-input"
import { Label } from "@/shared/ui/label"
import { Card, CardContent } from "@/shared/ui/card"
import { PageLoader } from '@/shared/ui/page-loader'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select"
import { Switch } from "@/shared/ui/switch"
import { Checkbox } from "@/shared/ui/checkbox"
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/contexts/AuthContext'
import {ErrorBoundary} from '@/shared/components'
import {WallClockTimeField} from '@/shared/ui/wall-clock-time-field'
import {
  applyWallClockHmToDate,
  buildReactBigCalendarFormats,
  dateToWallClockHm,
} from '@/utils/timeDisplayFormat'

import 'react-big-calendar/lib/css/react-big-calendar.css'

const locales = { 'en-US': enUS }
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales })
const bigCalendarFormats = buildReactBigCalendarFormats(format)

interface AvailabilityEvent {
  id: string
  title: string
  start: Date
  end: Date
  recurringId?: string
  recurrence?: {
    frequency: 'daily' | 'every7days' | '5daysWeek' | 'weekly' | 'custom'
    daysOfWeek?: number[]
    endDate?: Date
  }
  day_of_week: number
  start_time: string
  end_time: string
  slot_duration_minutes: number
  timezone: string
  effective_from: string
  effective_to: string | null
  project_id: string
  project_name: string
  moderator_id: string
}

interface Project {
  id: string
  name: string
}

const daysOfWeek = [
  { id: 0, name: 'Sunday' },
  { id: 1, name: 'Monday' },
  { id: 2, name: 'Tuesday' },
  { id: 3, name: 'Wednesday' },
  { id: 4, name: 'Thursday' },
  { id: 5, name: 'Friday' },
  { id: 6, name: 'Saturday' },
]

export function ModeratorAvailabilityCalendar() {
  const { user } = useAuth()
  const { toast } = useToast()
  const [events, setEvents] = useState<AvailabilityEvent[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [showEventDialog, setShowEventDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState<AvailabilityEvent | null>(null)
  const [moderatorId, setModeratorId] = useState<string | null>(null)
  const [isRecurring, setIsRecurring] = useState(false)
  const [recurrenceFrequency, setRecurrenceFrequency] = useState<'daily' | 'every7days' | '5daysWeek' | 'weekly' | 'custom'>('weekly')
  const [selectedDays, setSelectedDays] = useState<number[]>([])
  const [recurrenceEndDate, setRecurrenceEndDate] = useState<Date | null>(null)

  const [formData, setFormData] = useState({
    project_id: '',
    day_of_week: '',
    start_time: new Date().toISOString(),
    end_time: new Date(new Date().getTime() + 60 * 60 * 1000).toISOString(),
    slot_duration_minutes: '60',
    timezone: 'UTC',
    effective_from: new Date().toISOString().split('T')[0],
    effective_to: ''
  })

  useEffect(() => {
    fetchData()
  }, [user])

  const fetchData = async () => {
    try {
      if (!user) {
        return
      }

      // Get moderator ID
      const { data: moderator, error: moderatorError } = await supabase
        .from('moderators')
        .select('id')
        .eq('user_id', user.id)
        .single()

      if (moderatorError) {
        console.error('Moderator fetch error:', moderatorError)
        toast({
          title: "Error",
          description: `Failed to load moderator data: ${moderatorError.message}`,
          variant: "destructive"
        })
        return
      }

      if (!moderator) {
        toast({
          title: "Error",
          description: "You are not registered as a moderator",
          variant: "destructive"
        })
        return
      }
      setModeratorId(moderator.id)

      // Get projects where user is a moderator
      const { data: projectModerators, error: projectError } = await supabase
        .from('project_moderators')
        .select(`
          project_id, 
          projects (
            id, 
            name
          )
        `)
        .eq('moderator_id', moderator.id)

      if (projectError) {
        console.error('Project moderators fetch error:', projectError)
        toast({
          title: "Error",
          description: `Failed to load projects: ${projectError.message}`,
          variant: "destructive"
        })
      } else {
        // Extract projects from the nested structure
        const projectList = projectModerators
          .filter((pm: any) => pm.projects) // Filter out any null projects
          .map((pm: any) => ({
            id: pm.projects.id,
            name: pm.projects.name
          }))
        setProjects(projectList)
      }

      // If no projects found, try direct projects fetch as fallback
      if (projects.length === 0) {
        const { data: allProjects, error: allProjectsError } = await supabase
          .from('projects')
          .select('id, name')
          .limit(50) // Add limit to prevent timeouts
          
        if (allProjectsError) {
          console.error('Direct projects fetch error:', allProjectsError)
        } else if (allProjects && allProjects.length > 0) {
          setProjects(allProjects)
        } else {
          toast({
            title: "No Projects Found",
            description: "There are no projects available in the system.",
            variant: "destructive"
          })
        }
      }
    } catch (error) {
      console.error('Error in fetchData:', error)
      toast({
        title: "Error",
        description: "An unexpected error occurred while loading data",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const convertAvailabilityToEvents = (availabilities: any[]): AvailabilityEvent[] => {
    const events: AvailabilityEvent[] = []
    
    availabilities.forEach(availability => {
      // Create events for the next 4 weeks based on the availability pattern.
      // effective_from/effective_to are plain "yyyy-MM-dd" strings (no time
      // component) — parse with date-fns' `parse` (local midnight) rather
      // than `new Date(str)`, which treats bare date-only ISO strings as UTC.
      const startDate = parse(availability.effective_from, 'yyyy-MM-dd', new Date())
      const endDate = availability.effective_to
        ? parse(availability.effective_to, 'yyyy-MM-dd', new Date())
        : addWeeks(new Date(), 4)

      let currentDate = startDate

      while (currentDate.getTime() <= endDate.getTime()) {
        // Check if this date matches the day of week
        if (getDay(currentDate) === availability.day_of_week) {
          const dateStr = format(currentDate, 'yyyy-MM-dd')
          const startTime = dateStr + 'T' + availability.start_time
          const endTime = dateStr + 'T' + availability.end_time

          events.push({
            id: availability.id + '_' + dateStr,
            title: `${availability.projects?.name || 'Unknown Project'} - Available`,
            start: new Date(startTime),
            end: new Date(endTime),
            recurringId: availability.id,
            day_of_week: availability.day_of_week,
            start_time: availability.start_time,
            end_time: availability.end_time,
            slot_duration_minutes: availability.slot_duration_minutes,
            timezone: availability.timezone,
            effective_from: availability.effective_from,
            effective_to: availability.effective_to,
            project_id: availability.project_id,
            project_name: availability.projects?.name || 'Unknown Project',
            moderator_id: availability.moderator_id
          })
        }
        currentDate = addDays(currentDate, 1)
      }
    })
    
    return events
  }

  const generateRecurringEvents = (baseEvent: AvailabilityEvent): AvailabilityEvent[] => {
    const recurringEvents: AvailabilityEvent[] = []
    const startDate = baseEvent.start
    const endDate = baseEvent.recurrence?.endDate
      ? baseEvent.recurrence.endDate
      : addYears(startDate, 100) // Default to very far future if no end date

    let currentDate = startDate
    let count = 0

    while (currentDate.getTime() <= endDate.getTime()) {
      const shouldAddEvent = () => {
        switch (baseEvent.recurrence?.frequency) {
          case 'daily':
            return true
          case 'every7days':
            return count % 7 === 0
          case '5daysWeek':
            return getDay(currentDate) !== 0 && getDay(currentDate) !== 6
          case 'weekly':
            return getDay(currentDate) === getDay(startDate)
          case 'custom':
            return baseEvent.recurrence?.daysOfWeek?.includes(getDay(currentDate)) || false
          default:
            return false
        }
      }

      if (shouldAddEvent()) {
        recurringEvents.push({
          ...baseEvent,
          id: `${baseEvent.id}_${count}`,
          start: setMinutes(setHours(currentDate, getHours(startDate)), getMinutes(startDate)),
          end: setMinutes(setHours(currentDate, getHours(baseEvent.end)), getMinutes(baseEvent.end)),
        })
        count++
      }

      currentDate = addDays(currentDate, 1)
    }

    return recurringEvents
  }

  // Inclusive-both-ends range check (matches moment's `isBetween(a, b, undefined, '[]')`).
  const isWithinRangeInclusive = (x: Date, start: Date, end: Date): boolean =>
    x.getTime() >= start.getTime() && x.getTime() <= end.getTime()

  const checkForConflicts = (newEvent: Partial<AvailabilityEvent>, existingEvents: AvailabilityEvent[]): boolean => {
    if (!newEvent.start || !newEvent.end) return false
    const newStart = newEvent.start
    const newEnd = newEvent.end

    return existingEvents.some(event => {
      // Skip conflict check with the event being edited
      if (isEditing && event.id === selectedEvent?.id) return false

      // Skip conflict check with events in the same recurring series
      if (isEditing && selectedEvent?.recurringId && event.recurringId === selectedEvent.recurringId) return false

      return (
        isWithinRangeInclusive(newStart, event.start, event.end) ||
        isWithinRangeInclusive(newEnd, event.start, event.end) ||
        isWithinRangeInclusive(event.start, newStart, newEnd) ||
        isWithinRangeInclusive(event.end, newStart, newEnd)
      )
    })
  }

  // Build the base availability event from the current form state.
  const buildAvailabilityEvent = (
    startDateTime: Date,
    endDateTime: Date,
  ): AvailabilityEvent => {
    const projectName =
      projects.find(p => p.id === formData.project_id)?.name || 'Unknown Project'
    return {
      id: isEditing && selectedEvent ? selectedEvent.id : `new-${Date.now()}`,
      title: `${projectName} - Available`,
      start: startDateTime,
      end: endDateTime,
      recurringId: undefined,
      recurrence: undefined,
      day_of_week: parseInt(formData.day_of_week),
      start_time: formData.start_time,
      end_time: formData.end_time,
      slot_duration_minutes: parseInt(formData.slot_duration_minutes),
      timezone: formData.timezone,
      effective_from: formData.effective_from,
      effective_to: formData.effective_to || null,
      project_id: formData.project_id,
      project_name: projectName,
      moderator_id: moderatorId!,
    }
  }

  // Save a recurring availability series; returns false (and toasts) on conflict.
  const saveRecurringAvailability = (eventToSave: AvailabilityEvent): boolean => {
    eventToSave.recurringId = isEditing && selectedEvent?.recurringId
      ? selectedEvent.recurringId
      : `recurring-${eventToSave.id}`
    eventToSave.recurrence = {
      frequency: recurrenceFrequency,
      daysOfWeek: recurrenceFrequency === 'custom' ? selectedDays : undefined,
      endDate: recurrenceEndDate || undefined,
    }
    const recurringEvents = generateRecurringEvents(eventToSave)

    if (recurringEvents.some(event => checkForConflicts(event, events))) {
      toast({
        title: "Error",
        description: "One or more recurring events conflict with existing events. Please choose a different time or frequency.",
        variant: "destructive"
      })
      return false
    }

    if (isEditing && selectedEvent) {
      // Remove all events with the same recurringId
      const updatedEvents = events.filter(event => event.recurringId !== selectedEvent.recurringId)
      setEvents([...updatedEvents, ...recurringEvents])
    } else {
      setEvents([...events, ...recurringEvents])
    }

    toast({
      title: "Success",
      description: `Recurring availability ${isEditing ? 'updated' : 'added'} successfully`
    })
    return true
  }

  // Save a single availability event; returns false (and toasts) on conflict.
  const saveSingleAvailability = (eventToSave: AvailabilityEvent): boolean => {
    if (checkForConflicts(eventToSave, events)) {
      toast({
        title: "Error",
        description: "This event conflicts with an existing event. Please choose a different time.",
        variant: "destructive"
      })
      return false
    }

    if (isEditing && selectedEvent) {
      const updatedEvents = events.map(event =>
        event.id === selectedEvent.id ? eventToSave : event
      )
      setEvents(updatedEvents)
    } else {
      setEvents([...events, eventToSave])
    }
    toast({
      title: "Success",
      description: `Availability ${isEditing ? 'updated' : 'added'} successfully`
    })
    return true
  }

  const handleAddOrUpdateEvent = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!moderatorId) return

    try {
      const startDateTime = new Date(formData.start_time)
      const endDateTime = new Date(formData.end_time)

      if (endDateTime.getTime() <= startDateTime.getTime()) {
        toast({
          title: "Error",
          description: "End time must be after start time",
          variant: "destructive"
        })
        return
      }

      const eventToSave = buildAvailabilityEvent(startDateTime, endDateTime)
      const saved = isRecurring
        ? saveRecurringAvailability(eventToSave)
        : saveSingleAvailability(eventToSave)

      if (saved) resetForm()
    } catch (error) {
      console.error('Error in handleAddOrUpdateEvent:', error)
      toast({
        title: "Error",
        description: "Failed to save availability",
        variant: "destructive"
      })
    }
  }

  const resetForm = () => {
    setFormData({
      project_id: '',
      day_of_week: '',
      start_time: new Date().toISOString(),
      end_time: new Date(new Date().getTime() + 60 * 60 * 1000).toISOString(),
      slot_duration_minutes: '60',
      timezone: 'UTC',
      effective_from: new Date().toISOString().split('T')[0],
      effective_to: ''
    })
    setIsEditing(false)
    setSelectedEvent(null)
    setShowEventDialog(false)
    setIsRecurring(false)
    setRecurrenceFrequency('weekly')
    setSelectedDays([])
    setRecurrenceEndDate(null)
  }

  const handleSelectSlot = ({ start, end }: { start: Date; end: Date }) => {
    try {
      const dayOfWeek = getDay(start)

      setFormData({
        ...formData,
        day_of_week: dayOfWeek.toString(),
        start_time: start.toISOString(),
        end_time: end.toISOString(),
        effective_from: format(start, 'yyyy-MM-dd')
      })
      
      setIsEditing(false)
      setSelectedEvent(null)
      setShowEventDialog(true)
    } catch (error) {
      console.error('Error in handleSelectSlot:', error)
      toast({
        title: "Error",
        description: "Failed to open form. Please try again.",
        variant: "destructive"
      })
    }
  }

  const handleSelectEvent = (event: AvailabilityEvent) => {
    setFormData({
      project_id: event.project_id,
      day_of_week: event.day_of_week.toString(),
      start_time: event.start_time,
      end_time: event.end_time,
      slot_duration_minutes: event.slot_duration_minutes.toString(),
      timezone: event.timezone,
      effective_from: event.effective_from,
      effective_to: event.effective_to || ''
    })
    
    setIsEditing(true)
    setSelectedEvent(event)
    setIsRecurring(!!event.recurrence)
    if (event.recurrence) {
      setRecurrenceFrequency(event.recurrence.frequency)
      setSelectedDays(event.recurrence.daysOfWeek || [])
      setRecurrenceEndDate(event.recurrence.endDate || null)
    }
    setShowEventDialog(true)
  }

  const handleDeleteEvent = (deleteAll: boolean) => {
    if (selectedEvent) {
      if (deleteAll && selectedEvent.recurringId) {
        // Remove all events with the same recurringId
        const updatedEvents = events.filter(event => event.recurringId !== selectedEvent.recurringId)
        setEvents(updatedEvents)
        toast({
          title: "Success",
          description: "All recurring events deleted successfully"
        })
      } else {
        const updatedEvents = events.filter(event => event.id !== selectedEvent.id)
        setEvents(updatedEvents)
        toast({
          title: "Success",
          description: "Event deleted successfully"
        })
      }
      resetForm()
      setShowDeleteDialog(false)
    }
  }

  const eventStyleGetter = useCallback(
    (event: AvailabilityEvent) => {
      return {
        style: {
          backgroundColor: '#3182ce',
          borderRadius: '0px',
          opacity: 0.8,
          color: 'white',
          border: '0px',
          display: 'block'
        }
      }
    },
    []
  )

  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <PageLoader message="Loading" minHeight="min-h-[200px]" />
      </div>
    )
  }

  return (
    <ErrorBoundary>
      <div className="container mx-auto p-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold">Availability Calendar</h1>
          <div className="flex items-center gap-2">
            <div className="text-sm text-muted-foreground">
              Projects: {projects.length}
            </div>
            <Button onClick={fetchData} variant="outline" size="sm">
              Refresh Data
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4">
          <Card>
            <CardContent className="p-4">
              <BigCalendar
                localizer={localizer}
                formats={bigCalendarFormats}
                events={events}
                startAccessor="start"
                endAccessor="end"
                style={{ height: 'calc(100vh - 200px)' }}
                selectable
                onSelectSlot={handleSelectSlot}
                onSelectEvent={handleSelectEvent}
                views={[Views.MONTH, Views.WEEK, Views.DAY]}
                step={15}
                timeslots={4}
                eventPropGetter={eventStyleGetter}
                defaultView={Views.WEEK}
              />
            </CardContent>
          </Card>
        </div>

      <Dialog open={showEventDialog} onOpenChange={(open) => {
        setShowEventDialog(open)
        if (!open) resetForm()
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Availability' : 'Add New Availability'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddOrUpdateEvent} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="project_id">Project</Label>
              <div className="text-xs text-muted-foreground mb-2">
                {projects.length > 0 ? `${projects.length} project(s) available` : 'No projects found'}
              </div>
              <Select 
                value={formData.project_id || ''} 
                onValueChange={(value) => {
                  try {
                    setFormData({...formData, project_id: value})
                  } catch (error) {
                    console.error('Error updating project_id:', error)
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.length > 0 ? (
                    projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name || 'Unnamed Project'}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="no-projects" disabled>
                      No projects available
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="day_of_week">Day of Week</Label>
              <Select 
                value={formData.day_of_week || ''} 
                onValueChange={(value) => {
                  try {
                    setFormData({...formData, day_of_week: value})
                  } catch (error) {
                    console.error('Error updating day_of_week:', error)
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select day" />
                </SelectTrigger>
                <SelectContent>
                  {daysOfWeek.map((day) => (
                    <SelectItem key={day.id} value={day.id.toString()}>
                      {day.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="start_time">Start Time</Label>
              <WallClockTimeField
                id="start_time"
                value={
                  formData.start_time
                    ? dateToWallClockHm(new Date(formData.start_time))
                    : '00:00'
                }
                onChange={(hm) => {
                  try {
                    const currentDate = formData.start_time
                      ? new Date(formData.start_time)
                      : new Date();
                    setFormData({
                      ...formData,
                      start_time: applyWallClockHmToDate(currentDate, hm).toISOString(),
                    });
                  } catch (error) {
                    console.error('Error updating start time:', error);
                  }
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="end_time">End Time</Label>
              <WallClockTimeField
                id="end_time"
                value={
                  formData.end_time
                    ? dateToWallClockHm(new Date(formData.end_time))
                    : '01:00'
                }
                onChange={(hm) => {
                  try {
                    const currentDate = formData.end_time
                      ? new Date(formData.end_time)
                      : new Date();
                    setFormData({
                      ...formData,
                      end_time: applyWallClockHmToDate(currentDate, hm).toISOString(),
                    });
                  } catch (error) {
                    console.error('Error updating end time:', error);
                  }
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="slot_duration_minutes">Slot Duration (minutes)</Label>
              <Input
                id="slot_duration_minutes"
                type="number"
                min="15"
                max="240"
                step="15"
                value={formData.slot_duration_minutes}
                onChange={(e) => {
                  try {
                    setFormData({ ...formData, slot_duration_minutes: e.target.value })
                  } catch (error) {
                    console.error('Error updating slot_duration_minutes:', error)
                  }
                }}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="timezone">Timezone</Label>
              <Input
                id="timezone"
                value={formData.timezone}
                onChange={(e) => {
                  try {
                    setFormData({ ...formData, timezone: e.target.value })
                  } catch (error) {
                    console.error('Error updating timezone:', error)
                  }
                }}
                placeholder="UTC"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="effective_from">Effective From</Label>
              <DateInput
                id="effective_from"
                value={formData.effective_from}
                onChange={(e) => {
                  try {
                    setFormData({ ...formData, effective_from: e.target.value })
                  } catch (error) {
                    console.error('Error updating effective_from:', error)
                  }
                }}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="effective_to">Effective To (optional)</Label>
              <DateInput
                id="effective_to"
                value={formData.effective_to}
                minFromStartDate={formData.effective_from}
                onChange={(e) => {
                  try {
                    setFormData({ ...formData, effective_to: e.target.value })
                  } catch (error) {
                    console.error('Error updating effective_to:', error)
                  }
                }}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="recurring"
                checked={isRecurring}
                onCheckedChange={setIsRecurring}
              />
              <Label htmlFor="recurring">Recurring Event</Label>
            </div>

            {isRecurring && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="recurrenceFrequency">Recurrence Frequency</Label>
                  <Select 
                    value={recurrenceFrequency} 
                    onValueChange={(value: 'daily' | 'every7days' | '5daysWeek' | 'weekly' | 'custom') => {
                      try {
                        setRecurrenceFrequency(value)
                      } catch (error) {
                        console.error('Error updating recurrence frequency:', error)
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Every day</SelectItem>
                      <SelectItem value="every7days">Every 7 days</SelectItem>
                      <SelectItem value="5daysWeek">5 days a week (Mon-Fri)</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {recurrenceFrequency === 'custom' && (
                  <div className="space-y-2">
                    <Label>Select Days</Label>
                    <div className="flex flex-wrap gap-2">
                      {daysOfWeek.map((day) => (
                        <div key={day.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`day-${day.id}`}
                            checked={selectedDays.includes(day.id)}
                            onCheckedChange={(checked) => {
                              try {
                                if (checked) {
                                  setSelectedDays([...selectedDays, day.id])
                                } else {
                                  setSelectedDays(selectedDays.filter((id) => id !== day.id))
                                }
                              } catch (error) {
                                console.error('Error updating selected days:', error)
                              }
                            }}
                          />
                          <Label htmlFor={`day-${day.id}`}>{day.name}</Label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="recurrenceEnd">End Date (Optional)</Label>
                  <DateInput
                    id="recurrenceEnd"
                    value={recurrenceEndDate ? format(recurrenceEndDate, 'yyyy-MM-dd') : ''}
                    onChange={(e) => {
                      try {
                        setRecurrenceEndDate(e.target.value ? new Date(e.target.value) : null)
                      } catch (error) {
                        console.error('Error updating recurrence end date:', error)
                      }
                    }}
                  />
                </div>
              </>
            )}

            <DialogFooter>
              {isEditing && (
                <Button type="button" variant="destructive" onClick={() => setShowDeleteDialog(true)}>
                  Delete
                </Button>
              )}
              <Button type="submit">{isEditing ? 'Update' : 'Add'} Availability</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Event</DialogTitle>
          </DialogHeader>
          <p>Do you want to delete just this event or all recurring events?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleDeleteEvent(false)}>
              Delete This Event
            </Button>
            {selectedEvent?.recurringId && (
              <Button variant="destructive" onClick={() => handleDeleteEvent(true)}>
                Delete All Events
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </ErrorBoundary>
  )
}
